import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import Layout from "../component/Layout";
import { calculateOrderSummary } from "../utils/calculations";
const ORDER_OVERDUE_MS = 60000;

const tableHeaderStyle = {
  border: "1px solid #1f2937",
  padding: "4px 6px",
  textAlign: "left",
  fontSize: "11px",
  fontWeight: "bold",
  backgroundColor: "#f3f4f6",
  color: "#4b5563",
};

const tableCellStyle = {
  border: "1px solid #1f2937",
  padding: "4px 6px",
  fontSize: "12px",
};

const OrderRTD = () => {
  const [orders, setOrders] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  useEffect(() => {
    let cancelled = false;

    const fetchOrders = async () => {
      try {
        const q = query(
          collection(db, "orderList"),
          where("orderStatus", "==", "RTD"),
        );
        const querySnapshot = await getDocs(q);
        const ordersData = [];

        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          let orderTime = null;
          if (data.orderTime?.seconds) {
            orderTime = new Date(data.orderTime.seconds * 1000);
          } else if (data.addedAt) {
            orderTime = new Date(data.addedAt);
          }

          ordersData.push({
            docId: docSnap.id,
            ...data,
            orderTime,
            isOverdue: orderTime && new Date() - orderTime > ORDER_OVERDUE_MS,
          });
        });

        ordersData.sort((a, b) => (a.orderTime || 0) - (b.orderTime || 0));
        if (!cancelled) setOrders(ordersData);
      } catch (error) {
        console.error("Error fetching orders:", error);
        if (!cancelled) {
          setOrders([]);
          alert("Failed to load orders. Please refresh.");
        }
      }
    };

    fetchOrders();

    return () => {
      cancelled = true;
    };
  }, []);

  const groupedData = useMemo(() => {
    const totals = {};

    orders.forEach((order) => {
      const { buyerId, paymentMethod } = order;
      if (!buyerId) return;

      const summary = calculateOrderSummary(order);
      const key = buyerId;

      if (!totals[key]) {
        totals[key] = {
          buyerId,
          paymentMethods: new Set(),
          totalGrossWt: 0,
          totalNetWt: 0,
          totalSets: 0,
          totalAmount: 0,
          orders: [],
        };
      }

      if (paymentMethod) totals[key].paymentMethods.add(paymentMethod);
      totals[key].totalGrossWt += summary.gross;
      totals[key].totalNetWt += summary.net;
      totals[key].totalSets += summary.sets;
      totals[key].totalAmount += summary.amount;
      totals[key].orders.push(order);
    });

    const finalGroupedData = Object.values(totals).map((group) => ({
      ...group,
      paymentMethods: Array.from(group.paymentMethods).join(", ") || "N/A",
      totalGrossWt: group.totalGrossWt.toFixed(3),
      totalNetWt: group.totalNetWt.toFixed(3),
      totalSets: group.totalSets.toFixed(3),
      totalAmount: group.totalAmount.toFixed(2),
    }));

    return finalGroupedData;
  }, [orders]);

  const toggleGroup = (buyerId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [buyerId]: !prev[buyerId],
    }));
  };

  const formatDate = (date) => (date ? date.toLocaleString("en-IN") : "—");
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  const getAge = (orderTime) => {
    if (!orderTime) return "—";
    const diffMs = new Date() - orderTime;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };
  const getStatusStyle = (status) => ({
    display: "inline-block",
    padding: "2px 6px",
    fontSize: "11px",
    fontWeight: "bold",
    borderRadius: "4px",
    textTransform: "capitalize",
    backgroundColor: "#ede9fe",
    color: "#5b21b6",
  });

  const getSizeRows = (order) => {
    const rows = [];
    const variants = order.selectedVariants || [];
    const lots = order.lotDetails || [];
    const lotBySize = {};
    lots.forEach((l) => {
      if (l.size != null) lotBySize[l.size] = l;
    });

    if (variants.length) {
      variants.forEach((v) => {
        const lot = lotBySize[v.size];
        rows.push({
          size: v.size,
          qty: v.quantity,
          netWt: lot?.netWt ?? order.netWt,
          grossWt: lot?.grossWt ?? order.grossWt,
        });
      });
    } else if (lots.length) {
      lots.forEach((l) => {
        rows.push({
          size: l.size,
          qty: l.set,
          netWt: l.netWt,
          grossWt: l.grossWt,
        });
      });
    }
    return rows;
  };

  const grandTotals = useMemo(() => {
    return groupedData.reduce(
      (acc, group) => ({
        totalOrders: acc.totalOrders + group.orders.length,
        totalGrossWt: acc.totalGrossWt + parseFloat(group.totalGrossWt),
        totalNetWt: acc.totalNetWt + parseFloat(group.totalNetWt),
        totalAmount: acc.totalAmount + parseFloat(group.totalAmount),
      }),
      { totalOrders: 0, totalGrossWt: 0, totalNetWt: 0, totalAmount: 0 },
    );
  }, [groupedData]);

  return (
    <Layout pageTitle="Orders RTD Summary & Details">
      <div style={{ padding: "8px 0", overflowX: "auto" }}>
        {groupedData.length === 0 ? (
          <p
            style={{
              textAlign: "center",
              color: "#6b7280",
              paddingTop: "32px",
              fontSize: "18px",
            }}
          >
            No orders received with status RTD.
          </p>
        ) : (
          <div>
            <div
              style={{
                marginBottom: "12px",
                padding: "12px",
                backgroundColor: "#f0fdf4",
                borderRadius: "8px",
                border: "1px solid #86efac",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  flexWrap: "wrap",
                  fontSize: "13px",
                }}
              >
                <span>
                  <strong>Buyers:</strong> {groupedData.length}
                </span>
                <span>
                  <strong>Total Orders:</strong> {grandTotals.totalOrders}
                </span>
                <span>
                  <strong>Gross Wt:</strong>{" "}
                  {grandTotals.totalGrossWt.toFixed(3)}g
                </span>
                <span>
                  <strong>Net Wt:</strong> {grandTotals.totalNetWt.toFixed(3)}g
                </span>
                <span>
                  <strong>Amount:</strong>{" "}
                  {formatCurrency(grandTotals.totalAmount)}
                </span>
              </div>
            </div>

            <h2 style={{ marginBottom: "8px" }}>Summary by Buyer</h2>
            <p
              style={{
                marginBottom: "12px",
                color: "#4b5563",
                fontSize: "12px",
              }}
            >
              Click on a row to expand and see individual orders
            </p>

            {groupedData.map((group) => {
              const isExpanded = expandedGroups[group.buyerId];
              // const sizeRows = group.orders.map(getSizeRows);

              return (
                <div
                  key={group.buyerId}
                  style={{
                    marginBottom: "12px",
                    border: "1px solid #1f2937",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    onClick={() => toggleGroup(group.buyerId)}
                    style={{
                      backgroundColor: "#1f2937",
                      color: "#ffffff",
                      padding: "8px 12px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                      alignItems: "center",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#374151")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "#1f2937")
                    }
                  >
                    <strong
                      style={{
                        fontSize: "13px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span>{isExpanded ? "▼" : "▶"}</span>
                      Buyer: {group.buyerId}
                    </strong>
                    <span>Orders: {group.orders.length}</span>
                    <span>Sets: {group.totalSets}</span>
                    <span>Gross: {group.totalGrossWt}g</span>
                    <span>Net: {group.totalNetWt}g</span>
                    <span>Amount: ₹{group.totalAmount}</span>
                    <span>Methods: {group.paymentMethods}</span>
                  </div>

                  {isExpanded && (
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{ minWidth: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr>
                            <th style={tableHeaderStyle}>Image</th>
                            <th style={tableHeaderStyle}>Order ID</th>
                            <th style={tableHeaderStyle}>Product</th>
                            <th style={tableHeaderStyle}>Size</th>
                            <th style={tableHeaderStyle}>Qty</th>
                            <th style={tableHeaderStyle}>Net Wt (g)</th>
                            <th style={tableHeaderStyle}>Gross Wt (g)</th>
                            <th style={tableHeaderStyle}>Amount</th>
                            <th style={tableHeaderStyle}>Status</th>
                            <th style={tableHeaderStyle}>Order Time</th>
                            <th style={tableHeaderStyle}>Age</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.orders.map((order, idx) => {
                            const orderSizeRows = getSizeRows(order);
                            const summary = calculateOrderSummary(order);
                            const baseBg = order.isOverdue
                              ? "#fee2e2"
                              : idx % 2 === 0
                                ? "#ffffff"
                                : "#f9fafb";

                            return (
                              <React.Fragment key={order.docId || idx}>
                                <tr
                                  style={{
                                    backgroundColor: baseBg,
                                    borderBottom: "1px solid #e5e7eb",
                                  }}
                                >
                                  <td
                                    style={{
                                      ...tableCellStyle,
                                      textAlign: "center",
                                    }}
                                  >
                                    {order.images?.[0]?.url ? (
                                      <img
                                        src={order.images[0].url.trim()}
                                        alt="Product"
                                        style={{
                                          height: "24px",
                                          width: "24px",
                                          objectFit: "contain",
                                          border: "1px solid #d1d5db",
                                          borderRadius: "3px",
                                        }}
                                      />
                                    ) : (
                                      <span style={{ color: "#9ca3af" }}>
                                        —
                                      </span>
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      ...tableCellStyle,
                                      fontFamily: "monospace",
                                      fontSize: "11px",
                                    }}
                                  >
                                    {order.id || order.docId || "—"}
                                  </td>
                                  <td
                                    style={{
                                      ...tableCellStyle,
                                      fontWeight: "bold",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {order.productName || "—"}
                                  </td>
                                  <td style={tableCellStyle}>
                                    {orderSizeRows.length
                                      ? orderSizeRows.map((r, i) => (
                                          <div key={i}>{r.size}</div>
                                        ))
                                      : "—"}
                                  </td>
                                  <td style={tableCellStyle}>
                                    {orderSizeRows.length
                                      ? orderSizeRows
                                          .reduce(
                                            (s, r) =>
                                              s + (parseFloat(r.qty) || 0),
                                            0,
                                          )
                                          .toFixed(2)
                                      : "—"}
                                  </td>
                                  <td style={tableCellStyle}>
                                    {order.netWt
                                      ? parseFloat(order.netWt).toFixed(3)
                                      : summary.net?.toFixed(3) || "—"}
                                  </td>
                                  <td style={tableCellStyle}>
                                    {order.grossWt
                                      ? parseFloat(order.grossWt).toFixed(3)
                                      : summary.gross?.toFixed(3) || "—"}
                                  </td>
                                  <td
                                    style={{
                                      ...tableCellStyle,
                                      fontWeight: "600",
                                      color: "#1f2937",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ₹{summary.amount?.toFixed(2) || "—"}
                                  </td>
                                  <td style={tableCellStyle}>
                                    <span
                                      style={getStatusStyle(order.orderStatus)}
                                    >
                                      {order.orderStatus}
                                    </span>
                                  </td>
                                  <td
                                    style={{
                                      ...tableCellStyle,
                                      fontSize: "10px",
                                    }}
                                  >
                                    {formatDate(order.orderTime)}
                                  </td>
                                  <td
                                    style={{
                                      ...tableCellStyle,
                                      fontFamily: "monospace",
                                      fontSize: "10px",
                                    }}
                                  >
                                    {getAge(order.orderTime)}
                                  </td>
                                </tr>

                                {orderSizeRows.map((r, i) => (
                                  <tr
                                    key={`${order.docId || idx}-${i}`}
                                    style={{
                                      backgroundColor: "#fafafa",
                                      fontSize: "11px",
                                    }}
                                  >
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        padding: "2px 6px",
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        padding: "2px 6px",
                                      }}
                                    ></td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        paddingLeft: "16px",
                                        color: "#6b7280",
                                        fontSize: "11px",
                                      }}
                                      colSpan={2}
                                    >
                                      ↳ {r.size}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        color: "#6b7280",
                                        fontSize: "11px",
                                        padding: "2px 6px",
                                      }}
                                    >
                                      {r.qty}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        color: "#6b7280",
                                        fontSize: "11px",
                                        padding: "2px 6px",
                                      }}
                                    >
                                      {r.netWt
                                        ? parseFloat(r.netWt).toFixed(3)
                                        : "—"}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        color: "#6b7280",
                                        fontSize: "11px",
                                        padding: "2px 6px",
                                      }}
                                    >
                                      {r.grossWt
                                        ? parseFloat(r.grossWt).toFixed(3)
                                        : "—"}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                      }}
                                      colSpan={5}
                                    ></td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}

                          <tr
                            style={{
                              backgroundColor: "#dbeafe",
                              fontWeight: "bold",
                            }}
                          >
                            <td
                              style={{ ...tableCellStyle, fontWeight: "bold" }}
                              colSpan={3}
                            >
                              Total ({group.buyerId})
                            </td>
                            <td
                              style={{ ...tableCellStyle, fontWeight: "bold" }}
                            >
                              {group.totalSets}
                            </td>
                            <td
                              style={{ ...tableCellStyle, fontWeight: "bold" }}
                            >
                              {group.totalGrossWt}g
                            </td>
                            <td
                              style={{ ...tableCellStyle, fontWeight: "bold" }}
                            >
                              {group.totalNetWt}g
                            </td>
                            <td
                              style={{ ...tableCellStyle, fontWeight: "bold" }}
                            >
                              ₹{group.totalAmount}
                            </td>
                            <td
                              style={{ ...tableCellStyle, fontWeight: "bold" }}
                              colSpan={3}
                            ></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OrderRTD;
